/**
 * Game Selector Component
 * 
 * Component for selecting games to compare. Provides checkbox-based
 * selection interface with search, filtering, and batch selection options.
 * 
 * Features:
 * - Multi-select checkbox interface
 * - Search and filter games
 * - Batch selection controls
 * - Selected games preview
 * - Responsive design
 * 
 * @module TabPicker.GameSelector
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useComparisonStore, GameRef } from '../stores/comparisonStore';
import { useGetApiGames } from '../api/generated/games/games';
import { PrizeTracker } from './PrizeTracker';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';

/**
 * Format currency for display
 */
const formatCurrency = (value: string): string => {
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

/**
 * Individual game selection item
 */
const GameSelectionItem: React.FC<{
  game: GameRef & { 
    ticketPrice?: string; 
    totalTickets?: number;
    totalPayout?: string;
    createdAt?: string;
  };
  isSelected: boolean;
  onToggle: (gameId: number) => void;
  onEdit: (gameId: number) => void;
}> = ({ game, isSelected, onToggle, onEdit }) => {
  return (
    <div 
      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
        isSelected 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={() => onToggle(game.id)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(game.id)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
          <div>
            <h3 className="font-medium text-xl tablet:text-lg text-gray-900">{game.name}</h3>
            <div className="flex items-center gap-4 mt-1 text-base tablet:text-sm text-gray-500">
              {game.ticketPrice && (
                <span>Price: {formatCurrency(game.ticketPrice)}</span>
              )}
              {game.totalTickets && (
                <span>Tickets: {game.totalTickets?.toLocaleString() || 'N/A'}</span>
              )}
              {game.totalPayout && (
                <span>Payout: {formatCurrency(game.totalPayout)}</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {game.createdAt && (
            <div className="text-sm tablet:text-xs text-gray-400">
              {new Date(game.createdAt).toLocaleDateString()}
            </div>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(game.id);
            }}
            variant="secondary"
            size="sm"
            className="text-sm tablet:text-xs"
          >
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Selected games preview
 */
const SelectedGamesPreview: React.FC<{
  selectedGames: GameRef[];
  onRemove: (gameId: number) => void;
}> = ({ selectedGames, onRemove }) => {
  if (selectedGames.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6">
      <div className="px-4 py-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-xl tablet:text-lg text-gray-900">
            Selected Games
          </h3>
          <Badge variant="default">
            {selectedGames.length}/10
          </Badge>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {selectedGames.map((game) => (
            <Badge
              key={game.id}
              variant="default"
              className="flex items-center gap-1 px-2 py-1"
            >
              <span>{game.name}</span>
              <button
                onClick={() => onRemove(game.id)}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>
    </Card>
  );
};

/**
 * Deal Status Controls - Prize Tracking and Jar Fill
 */
export const DealStatusControls: React.FC<{
  selectedGames: GameRef[];
}> = ({ selectedGames }) => {
  const { 
    config: { dealStatuses = [] },
    setGameDealStatus,
    clearAllDealStatuses
  } = useComparisonStore();
  
  // Get full game data with payout structures
  const { data: gamesData } = useGetApiGames();

  if (selectedGames.length === 0) {
    return null;
  }

  const getDealStatusForGame = (gameId: number) => {
    return dealStatuses.find(ds => ds.gameId === gameId);
  };
  
  const getFullGameData = (gameId: number) => {
    const game = gamesData?.games?.find(game => game.id === gameId);
    return game;
  };

  const handleJarFillChange = (gameId: number, percentage: number) => {
    setGameDealStatus(gameId, percentage);
  };

  return (
    <Card className="mb-6">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-medium text-xl tablet:text-lg text-gray-900">
              Deal Status
            </h3>
            <p className="text-sm tablet:text-sm text-gray-500 mt-1">
              Mark which prizes have already been claimed and estimate how many tickets are left in the jar
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={clearAllDealStatuses}
              variant="secondary"
              size="sm"
              disabled={dealStatuses.length === 0}
            >
              Reset All
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {selectedGames.map((game) => {
            const dealStatus = getDealStatusForGame(game.id);
            const fillPercentage = dealStatus?.jarFillPercentage ?? 100; // Default to 100% (fresh jar)
            
            return (
              <div key={game.id} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-xl tablet:text-lg text-gray-900">{game.name}</h4>
                    <p className="text-base tablet:text-sm text-gray-600">
                      Current jar fill: <span className="font-medium">{fillPercentage}%</span>
                    </p>
                  </div>
                  <div className="text-base tablet:text-sm text-gray-500">
                    {fillPercentage === 100 && "🟢 Fresh Jar"}
                    {fillPercentage >= 75 && fillPercentage < 100 && "🟡 Mostly Full"}
                    {fillPercentage >= 50 && fillPercentage < 75 && "🟠 Half Full"}
                    {fillPercentage >= 25 && fillPercentage < 50 && "🔴 Quarter Full"}
                    {fillPercentage < 25 && "⚫ Nearly Empty"}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={fillPercentage}
                    onChange={(e) => handleJarFillChange(game.id, parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={fillPercentage}
                    onChange={(e) => handleJarFillChange(game.id, parseInt(e.target.value) || 0)}
                    className="w-16 text-center"
                  />
                  <span className="text-gray-500 text-sm">%</span>
                </div>
                
                <div className="flex justify-between text-sm tablet:text-xs text-gray-400 mt-1">
                  <span>Empty (0%)</span>
                  <span>Fresh (100%)</span>
                </div>
                
                {/* Prize Tracker - Individual prize tracking */}
                {(() => {
                  const fullGameData = getFullGameData(game.id);
                  return fullGameData?.payoutStructure ? (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="font-medium text-xl tablet:text-lg text-gray-900 mb-3">Prize Tracking</h5>
                      <PrizeTracker
                        gameId={game.id}
                        payoutStructure={fullGameData.payoutStructure}
                      />
                    </div>
                  ) : (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-base tablet:text-sm text-gray-500 italic">
                        Prize data not available for this game
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-start gap-2">
            <div className="text-blue-600 text-base tablet:text-sm">💡</div>
            <div className="text-base tablet:text-sm text-blue-700">
              <strong>Tip:</strong> Lower percentages mean fewer prizes remaining, 
              which typically results in worse odds. Adjust based on what you observe 
              at the store or information from other players.
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Main Game Selector Component
 */
export const GameSelector: React.FC<{
  games: Array<GameRef & { 
    ticketPrice?: string; 
    totalTickets?: number;
    totalPayout?: string;
    createdAt?: string;
  }>;
  className?: string;
  showDealStatus?: boolean;
}> = ({ games, className = '', showDealStatus = false }) => {
  const navigate = useNavigate();
  const {
    selectedGameIds,
    toggleGameSelection,
    selectAllGames,
    clearSelection,
    deselectGame,
    canCompare,
  } = useComparisonStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'price'>('name');

  const handleEditGame = (gameId: number) => {
    navigate(`/edit-game/${gameId}`);
  };

  // Filter and sort games
  const filteredAndSortedGames = useMemo(() => {
    let filtered = games.filter((game) =>
      game.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort games
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          if (!a.createdAt || !b.createdAt) return 0;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'price':
          if (!a.ticketPrice || !b.ticketPrice) return 0;
          return parseFloat(b.ticketPrice) - parseFloat(a.ticketPrice);
        default:
          return 0;
      }
    });

    return filtered;
  }, [games, searchTerm, sortBy]);

  // Get selected games with details
  const selectedGames = games.filter(game => 
    selectedGameIds.includes(game.id)
  );

  const selectedCount = selectedGameIds.length;
  const maxGames = 10;
  const minGames = 2;

  if (games.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-gray-400 text-4xl mb-4">🎲</div>
        <h3 className="text-2xl tablet:text-lg font-semibold text-gray-700 mb-2">
          No Games Available
        </h3>
        <p className="text-xl tablet:text-lg text-gray-500">
          Create some games first to use the comparison feature
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Selected Games Preview */}
      <SelectedGamesPreview 
        selectedGames={selectedGames}
        onRemove={deselectGame}
      />

      {/* Deal Status Controls - only shown when showDealStatus is true */}
      {showDealStatus && (
        <DealStatusControls 
          selectedGames={selectedGames}
        />
      )}

      {/* Controls */}
      <div className="flex flex-col tablet:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search games..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <label className="text-base tablet:text-sm font-medium whitespace-nowrap">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md text-base tablet:text-sm"
          >
            <option value="name">Name</option>
            <option value="created">Date Created</option>
            <option value="price">Ticket Price</option>
          </select>
        </div>
      </div>

      {/* Batch Selection Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={selectAllGames}
            variant="secondary"
            size="sm"
            disabled={games.length === 0}
          >
            Select All
          </Button>
          <Button
            onClick={clearSelection}
            variant="secondary"
            size="sm"
            disabled={selectedCount === 0}
          >
            Clear Selection
          </Button>
        </div>

        <div className="text-base tablet:text-sm text-gray-600 ml-4">
          {selectedCount} of {games.length} games selected
          {selectedCount >= minGames && (
            <span className="text-green-600 ml-2">
              ✓ Ready to compare
            </span>
          )}
          {selectedCount < minGames && (
            <span className="text-orange-600 ml-2">
              Need {minGames - selectedCount} more
            </span>
          )}
        </div>
      </div>

      {/* Games List */}
      <div className="space-y-3">
        {filteredAndSortedGames.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-2xl mb-2">🔍</div>
            <p className="text-xl tablet:text-lg text-gray-500">
              No games match your search criteria
            </p>
          </div>
        ) : (
          filteredAndSortedGames.map((game) => (
            <GameSelectionItem
              key={game.id}
              game={game}
              isSelected={selectedGameIds.includes(game.id)}
              onToggle={toggleGameSelection}
              onEdit={handleEditGame}
            />
          ))
        )}
      </div>

      {/* Selection Status */}
      {selectedCount > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base tablet:text-sm font-medium text-gray-700">
                {selectedCount} game{selectedCount !== 1 ? 's' : ''} selected
              </div>
              <div className="text-sm tablet:text-xs text-gray-500 mt-1">
                {selectedCount < minGames && `Need ${minGames - selectedCount} more games to compare`}
                {selectedCount >= minGames && selectedCount <= maxGames && 'Ready for comparison'}
                {selectedCount > maxGames && `Remove ${selectedCount - maxGames} games (max ${maxGames})`}
              </div>
            </div>

            {canCompare() && (
              <Badge variant="success">
                Ready to Compare
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};